import React, { useState, FC } from 'react'
import { Trans, useLingui } from '@lingui/react/macro'

import { XStack, YStack, useThemeName } from 'tamagui'
import { Text } from '@newComponents/Text'

import { Grid } from '@newComponents/Grid'
import { Separator } from '@newComponents/Separator'
import { Link, ExternalLink } from '@newComponents/Link'

import { IconWrapper } from '@newComponents/Icon/IconWrapper'
import WindowNew from '@newComponents/Icon/icons/WindowNew'

import { getPurpleBoxShadow } from './UnauthenticatedHomeScreen/styles'

export const HomeScreenFeaturesCard: FC = () => {
  const isDark = useThemeName() === 'dark'

  return (
    <YStack
      padding='$6'
      backgroundColor='$backgroundSoft'
      borderRadius='$8'
      style={{ boxShadow: getPurpleBoxShadow(isDark) } as any}
    >
      <YStack gap='$10'>
        <YStack gap='$8' $gtXs={{ display: 'none' }}>
          <GuildsAndNetworks />

          <Events />

          <Presentations />
        </YStack>
        <YStack display='none' $gtXs={{ display: 'flex' }}>
          <Grid gap='$6'>
            <Grid.Column>
              <GuildsAndNetworks />
            </Grid.Column>

            <Grid.Column>
              <Events />
            </Grid.Column>

            <Grid.Column>
              <Presentations />
            </Grid.Column>
          </Grid>
        </YStack>

        <YStack alignItems='center'>
          <Separator borderBottomColor='$borderColor' width={60} />
        </YStack>

        <YStack alignItems='center'>
          <Text fontSize='$4' color='$colorMuted'>
            <Trans>Not sure about a feature?</Trans>
          </Text>

          <Text fontSize='$4' color='$colorMuted' textAlign='center'>
            <Trans>
              <ExternalLink href='mailto:hi@guild.host'>
                <Text fontSize='$4' color='$colorMuted'>
                  Get in touch
                </Text>
              </ExternalLink>
              , we'd love to chat with you!
            </Trans>
          </Text>
        </YStack>
      </YStack>
    </YStack>
  )
}

const GuildsAndNetworks: FC = () => {
  const isDark = useThemeName() === 'dark'
  const accentColor = isDark ? '#A78BFA' : '#5533EB'
  const circleBg = isDark
    ? 'rgba(167, 139, 250, 0.2)'
    : 'rgba(85, 51, 235, 0.1)'
  const { t } = useLingui()

  return (
    <YStack gap='$4'>
      <Text
        fontSize='$6'
        fontWeight='600'
        fontFamily='$body'
        color={accentColor}
      >
        <Trans>Guilds & Networks</Trans>
      </Text>

      <YStack gap='$2'>
        {[
          <FeatureLinkBulletItem
            href='/docs/guilds/overview'
            label={t`Create a Following`}
          />,
          <FeatureLinkBulletItem
            href='/docs/guilds/members'
            label={t`Manage your Members`}
          />,
          <FeatureLinkBulletItem
            href='/docs/guilds/members'
            label={t`3-tier Permissions System`}
          />,
          <FeatureLinkBulletItem
            href='/docs/guilds/forms'
            label={t`Membership Forms & Approvals`}
          />,
          <FeatureLinkBulletItem
            href='/docs/guilds/overview'
            label={t`Inter-connect with Networks`}
          />,
          <FeatureLinkBulletItem
            href='/docs/guilds/overview'
            label={t`Cross-promote via Networks`}
          />,
          <FeatureLinkBulletItem
            href='/docs/guilds/monetization'
            label={t`Accept Donations`}
          />,
          <FeatureLinkBulletItem
            href='/docs/guilds/monetization'
            label={t`Sponsorships & Analytics`}
          />,
          <FeatureLinkBulletItem
            href='/docs/guilds/messaging'
            label={t`Message your Members`}
          />,
          <FeatureLinkBulletItem
            href='/docs/guilds/settings'
            label={t`Notify your Slack`}
          />,
          <FeatureLinkBulletItem
            href='/docs/guilds/profile'
            label={t`Themed Guild Pages`}
          />,
          <FeatureLinkBulletItem
            href='/docs/guilds/profile'
            label={t`Beautiful Social Sharing`}
          />,
          <FeatureLinkBulletItem
            href='/docs/guilds/embeds'
            label={t`Embeddable Widgets`}
          />,
          <FeatureLinkBulletItem
            href='/docs/guilds/bluesky'
            label={t`Announce on Bluesky`}
          />,
          <FeatureLinkBulletItem
            href='/docs/guilds/meetup'
            label={t`Mirror your Meetup.com events`}
          />,
          <FeatureLinkBulletItem
            href='/docs/guilds/google-calendar'
            label={t`Google Calendar Sync`}
          />,
          <FeatureLinkBulletItem
            href='/api/next/docs'
            label={t`API Access`}
            external
          />,
        ].map((bullet, index) => (
          <XStack gap='$2' key={index}>
            <XStack
              width={18}
              minWidth={18}
              height={18}
              minHeight={18}
              backgroundColor={circleBg}
              alignItems='center'
              justifyContent='center'
              borderRadius={9}
            >
              <Text color={accentColor}>✓</Text>
            </XStack>

            {bullet}
          </XStack>
        ))}
      </YStack>
    </YStack>
  )
}

const FeatureLinkBulletItem = ({
  href,
  label,
  external = false,
}: {
  href: string
  label: string
  external?: boolean
}) => {
  const [isHovered, setIsHovered] = useState(false)

  const WrappingComponent = external ? ExternalLink : Link

  return (
    <WrappingComponent
      href={href}
      flex={1}
      onHoverIn={() => setIsHovered(true)}
      onHoverOut={() => setIsHovered(false)}
      size='$4'
      animation='medium'
      color='$color'
      hoverStyle={{ color: '$primaryColor', textDecorationLine: 'none' }}
    >
      <XStack gap='$2'>
        <Text flex={1} size='$4' color='inherit'>
          {label}
        </Text>

        <XStack
          animation='medium'
          x={isHovered ? 4 : 0}
          width='$4'
          height='$4'
          flexShrink={0}
        >
          <XStack opacity={0.4} position='absolute'>
            <IconWrapper size='$4' color='$color'>
              <WindowNew />
            </IconWrapper>
          </XStack>

          <XStack
            animation='medium'
            opacity={isHovered ? 0.8 : 0}
            position='absolute'
          >
            <IconWrapper size='$4' color='$primaryColor'>
              <WindowNew />
            </IconWrapper>
          </XStack>
        </XStack>
      </XStack>
    </WrappingComponent>
  )
}

const Events: FC = () => {
  const isDark = useThemeName() === 'dark'
  const accentColor = isDark ? '#F5A0F6' : '#F26AF4'
  const circleBg = isDark
    ? 'rgba(242, 106, 244, 0.2)'
    : 'rgba(242, 106, 244, 0.1)'
  const { t } = useLingui()

  return (
    <YStack gap='$4'>
      <Text
        fontSize='$6'
        fontWeight='600'
        fontFamily='$body'
        color={accentColor}
      >
        <Trans>Events</Trans>
      </Text>

      <YStack gap='$2'>
        {[
          <FeatureLinkBulletItem
            href='/docs/events/overview'
            label={t`In-Person Events`}
          />,
          <FeatureLinkBulletItem
            href='/docs/events/overview'
            label={t`Online Events`}
          />,
          <FeatureLinkBulletItem
            href='/docs/events/overview'
            label={t`Hybrid In-Person & Online Events`}
          />,
          <FeatureLinkBulletItem
            href='/docs/events/multi-host'
            label={t`Multi-Host Events`}
          />,
          <FeatureLinkBulletItem
            href='/docs/events/tiers'
            label={t`Multi-Tier Ticketing`}
          />,
          <FeatureLinkBulletItem
            href='/docs/events/discounts'
            label={t`Discount Codes`}
          />,
          <FeatureLinkBulletItem
            href='/docs/events/orders'
            label={t`Refunds & Order Management`}
          />,
          <FeatureLinkBulletItem
            href='/docs/events/rsvps'
            label={t`Optional Waitlist`}
          />,
          <FeatureLinkBulletItem
            href='/docs/events/attendee-forms'
            label={t`Attendance Forms & Approvals`}
          />,
          <FeatureLinkBulletItem
            href='/docs/events/messages'
            label={t`Message your Attendees`}
          />,
          <FeatureLinkBulletItem
            href='/docs/events/overview'
            label={t`Connect Presentations`}
          />,
          <FeatureLinkBulletItem
            href='/docs/events/overview'
            label={t`Calendar Sync & Export`}
          />,
          <FeatureLinkBulletItem
            href='/docs/events/page'
            label={t`Themed Event Pages`}
          />,
          <FeatureLinkBulletItem
            href='/docs/events/overview'
            label={t`Beautiful Social Sharing`}
          />,
          <FeatureLinkBulletItem
            href='/docs/events/embeds'
            label={t`Embeddable Widgets`}
          />,
          <FeatureLinkBulletItem
            href='/api/next/docs'
            label={t`API Access`}
            external
          />,
        ].map((bullet, index) => (
          <XStack gap='$2' key={index}>
            <XStack
              width={18}
              minWidth={18}
              height={18}
              minHeight={18}
              backgroundColor={circleBg}
              alignItems='center'
              justifyContent='center'
              borderRadius={9}
            >
              <Text color={accentColor}>✓</Text>
            </XStack>

            {bullet}
          </XStack>
        ))}
      </YStack>
    </YStack>
  )
}

const Presentations: FC = () => {
  const isDark = useThemeName() === 'dark'
  const accentColor = isDark ? '#FBAD7E' : '#F9894B'
  const circleBg = isDark
    ? 'rgba(249, 137, 75, 0.2)'
    : 'rgba(249, 137, 75, 0.1)'
  const { t } = useLingui()

  return (
    <YStack gap='$4'>
      <Text
        fontSize='$6'
        fontWeight='600'
        fontFamily='$body'
        color={accentColor}
      >
        <Trans>Presentations</Trans>
      </Text>

      <YStack gap='$2'>
        {[
          <FeatureLinkBulletItem
            href='/docs/presentations/overview'
            label={t`Manage your Guild's Content Library`}
          />,
          <FeatureLinkBulletItem
            href='/docs/presentations/overview'
            label={t`Manage your Personal Content Library`}
          />,
          <FeatureLinkBulletItem
            href='/docs/presentations/overview'
            label={t`Connect Presentations to Events`}
          />,
          <FeatureLinkBulletItem
            href='/docs/presentations/overview'
            label={t`Connect Presentations to Guilds`}
          />,
          <FeatureLinkBulletItem
            href='/docs/presentations/claiming'
            label={t`Claim Presentations`}
          />,
          <FeatureLinkBulletItem
            href='/docs/presentations/creation'
            label={t`Connect Speakers & Bios`}
          />,
          <FeatureLinkBulletItem
            href='/docs/presentations/page'
            label={t`Video Links (YouTube, Vimeo)`}
          />,
          <FeatureLinkBulletItem
            href='/docs/presentations/overview'
            label={t`Curate Content from top Presenters`}
          />,
          <FeatureLinkBulletItem
            href='/docs/presentations/overview'
            label={t`Share Content across Networks`}
          />,
          <FeatureLinkBulletItem
            href='/docs/presentations/page'
            label={t`Themed Presentation Pages`}
          />,
          <FeatureLinkBulletItem
            href='/docs/presentations/overview'
            label={t`Beautiful Social Sharing`}
          />,
          <FeatureLinkBulletItem
            href='/docs/presentations/embeds'
            label={t`Embeddable Widgets`}
          />,
          <FeatureLinkBulletItem
            href='/api/next/docs'
            label={t`API Access`}
            external
          />,
        ].map((bullet, index) => (
          <XStack gap='$2' key={index}>
            <XStack
              width={18}
              minWidth={18}
              height={18}
              minHeight={18}
              backgroundColor={circleBg}
              alignItems='center'
              justifyContent='center'
              borderRadius={9}
            >
              <Text color={accentColor}>✓</Text>
            </XStack>

            {bullet}
          </XStack>
        ))}
      </YStack>
    </YStack>
  )
}
